import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import type { BarcodeType, CodeFormat } from '../utils/barcodeFormats';
import { getAdminId as resolveAdminId } from '../utils/getAdminId';

export interface BarcodeHistoryRecord {
  id: string;
  admin_id: string;
  product_id: string | null;
  barcode_value: string;
  code_type: BarcodeType;
  code_format: CodeFormat;
  product_name: string;
  category: string | null;
  location: string | null;
  bin_id: string | null;
  print_count: number;
  label_template: string | null;
  created_by: string;
  created_at: string;
}

export interface BarcodeHistoryStats {
  totalGenerated: number;
  thisMonth: number;
  mostUsedFormat: BarcodeType | null;
  recentActivity: BarcodeHistoryRecord[];
}

export const useBarcodeHistory = () => {
  const { user } = useAuth();
  const [history, setHistory] = useState<BarcodeHistoryRecord[]>([]);
  const [stats, setStats] = useState<BarcodeHistoryStats>({
    totalGenerated: 0,
    thisMonth: 0,
    mostUsedFormat: null,
    recentActivity: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getAdminId = useCallback(() => {
    if (!user) return null;
    return resolveAdminId(user);
  }, [user]);

  const fetchHistory = useCallback(async (filters?: {
    search?: string;
    codeType?: BarcodeType;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    offset?: number;
  }) => {
    const adminId = getAdminId();
    if (!adminId) return;

    setLoading(true);
    setError(null);

    try {
      if (filters?.search && filters.search.trim()) {
        const { data, error: fetchError } = await supabase.rpc('search_barcode_history', {
          p_admin_id: adminId,
          p_search_term: filters.search.trim(),
          p_code_type: filters.codeType || null,
          p_date_from: filters.dateFrom || null,
          p_date_to: filters.dateTo || null,
          p_limit: filters.limit || 50,
          p_offset: filters.offset || 0
        });

        if (fetchError) throw fetchError;
        setHistory(data || []);
        return;
      }

      let query = supabase
        .from('barcode_history')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false });

      if (filters?.codeType) {
        query = query.eq('code_type', filters.codeType);
      }

      if (filters?.dateFrom) {
        query = query.gte('created_at', filters.dateFrom);
      }

      if (filters?.dateTo) {
        query = query.lte('created_at', filters.dateTo);
      }

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      if (filters?.offset) {
        query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      setHistory(data || []);
    } catch (err) {
      console.error('Error fetching barcode history:', err);
      setError('Failed to load barcode history');
    } finally {
      setLoading(false);
    }
  }, [getAdminId]);

  const fetchStats = useCallback(async () => {
    const adminId = getAdminId();
    if (!adminId) return;

    try {
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { data: totalData } = await supabase
        .from('barcode_history')
        .select('id', { count: 'exact' })
        .eq('admin_id', adminId);

      const { data: monthData } = await supabase
        .from('barcode_history')
        .select('id', { count: 'exact' })
        .eq('admin_id', adminId)
        .gte('created_at', startOfMonth.toISOString());

      const { data: formatData } = await supabase
        .from('barcode_history')
        .select('code_type')
        .eq('admin_id', adminId);

      const { data: recentData } = await supabase
        .from('barcode_history')
        .select('*')
        .eq('admin_id', adminId)
        .order('created_at', { ascending: false })
        .limit(5);

      let mostUsedFormat: BarcodeType | null = null;
      if (formatData && formatData.length > 0) {
        const formatCounts: Record<string, number> = {};
        formatData.forEach(record => {
          formatCounts[record.code_type] = (formatCounts[record.code_type] || 0) + 1;
        });
        const maxCount = Math.max(...Object.values(formatCounts));
        mostUsedFormat = Object.keys(formatCounts).find(
          key => formatCounts[key] === maxCount
        ) as BarcodeType || null;
      }

      setStats({
        totalGenerated: totalData?.length || 0,
        thisMonth: monthData?.length || 0,
        mostUsedFormat,
        recentActivity: recentData || []
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [getAdminId]);

  const addToHistory = useCallback(async (record: {
    product_id?: string;
    barcode_value: string;
    code_type: BarcodeType;
    code_format: CodeFormat;
    product_name: string;
    category?: string;
    location?: string;
    bin_id?: string;
    label_template?: string;
  }) => {
    const adminId = getAdminId();
    if (!adminId || !user) return null;

    try {
      const { data, error: insertError } = await supabase
        .from('barcode_history')
        .insert({
          admin_id: adminId,
          product_id: record.product_id || null,
          barcode_value: record.barcode_value,
          code_type: record.code_type,
          code_format: record.code_format,
          product_name: record.product_name,
          category: record.category || null,
          location: record.location || null,
          bin_id: record.bin_id || null,
          label_template: record.label_template || null,
          created_by: user.id,
          print_count: 0
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setHistory(prev => [data, ...prev]);
      fetchStats();

      return data;
    } catch (err) {
      console.error('Error adding to history:', err);
      return null;
    }
  }, [getAdminId, user, fetchStats]);

  const incrementPrintCount = useCallback(async (recordId: string) => {
    try {
      const { error: updateError } = await supabase
        .rpc('increment_barcode_print_count', { p_barcode_id: recordId });

      if (updateError) throw updateError;

      setHistory(prev => prev.map(record =>
        record.id === recordId
          ? { ...record, print_count: record.print_count + 1 }
          : record
      ));
    } catch (err) {
      console.error('Error incrementing print count:', err);
    }
  }, []);

  const deleteFromHistory = useCallback(async (recordId: string) => {
    try {
      const { error: deleteError } = await supabase
        .from('barcode_history')
        .delete()
        .eq('id', recordId);

      if (deleteError) throw deleteError;

      setHistory(prev => prev.filter(record => record.id !== recordId));
      fetchStats();

      return true;
    } catch (err) {
      console.error('Error deleting from history:', err);
      return false;
    }
  }, [fetchStats]);

  useEffect(() => {
    if (user) {
      fetchHistory({ limit: 50 });
      fetchStats();
    }
  }, [user, fetchHistory, fetchStats]);

  return {
    history,
    stats,
    loading,
    error,
    fetchHistory,
    fetchStats,
    addToHistory,
    incrementPrintCount,
    deleteFromHistory
  };
};
